import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { TourOverlay } from '@/features/tour/TourOverlay.js';
import { TourContext, useAutoTour, type ActiveTour } from '@/features/tour/context.js';
import { markTourSeen } from '@/features/tour/storage.js';
import type { TourId } from '@/features/tour/types.js';
import { useSignedOut } from '@/lib/session.js';

export interface TourProviderProps {
  /** Undefined while the account loads: no tour runs until it is known. */
  customerId: string | undefined;
  name?: string;
  children: ReactNode;
}

/**
 * Runs the guided tours (MYP-83): which one is on screen, which step, and who has seen what.
 *
 * Sits in `Layout`, around the header and the page, because the welcome tour points at both and the
 * page tours read which step is showing (`useTourStep`). The popover itself renders here, after the
 * page, so it lands above any panel the page has open.
 */
export function TourProvider({ customerId, name, children }: TourProviderProps) {
  const [active, setActive] = useState<ActiveTour | null>(null);
  const [seenVersion, setSeenVersion] = useState(0);
  // The popover sits outside `SessionGate`, so it is hidden here once the session ends — it would
  // otherwise stay over the "Please sign in again" screen. Not marked seen: the tour was not done.
  const signedOut = useSignedOut();

  const start = useCallback((id: TourId) => setActive({ id, index: 0 }), []);
  const go = useCallback(
    (index: number) => setActive((current) => (current ? { ...current, index } : current)),
    [],
  );
  const finish = useCallback(() => {
    if (active && customerId) markTourSeen(active.id, customerId);
    setActive(null);
    setSeenVersion((version) => version + 1);
  }, [active, customerId]);

  const value = useMemo(
    () => ({ active, customerId, seenVersion, start, go, finish }),
    [active, customerId, seenVersion, start, go, finish],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      <WelcomeTour />
      {active && customerId && !signedOut ? (
        <TourOverlay
          tour={active.id}
          index={active.index}
          customerId={customerId}
          {...(name ? { name } : {})}
          onGo={go}
          onFinish={finish}
        />
      ) : null}
    </TourContext.Provider>
  );
}

/** The welcome tour opens on a customer's first visit, whatever page that is. */
function WelcomeTour() {
  useAutoTour('welcome');
  return null;
}
